/**
 * Country → native ISO 4217 currency map. Marketplace-wide source of truth.
 *
 * Authoritative mirror of `native_currency()` in
 * `supabase/migrations/20260623120000_k6_bundled_currency_pin_snapshots.sql`.
 * The two MUST stay in sync — drift is caught by a fitness test introduced
 * in slice C2 (PLAN §K.6 9.a).
 *
 * Distinct from `lib/shipping/origin-support.ts`, which is the narrower
 * "Shippo-rateable origins" map (US/GB/DE/FR — verified 2026-06-20). This
 * file covers ALL countries the marketplace accepts sellers from for
 * currency pinning, regardless of live-rate availability.
 *
 * Returns `null` for any country not in the map. Server-side: the DB
 * trigger then RAISEs — listings cannot be saved with NULL currency.
 *
 * @see .tondocs/shippo/PLAN.md §A "Currency-country pin (all listings)"
 * @see .tondocs/shippo/PLAN.md §K.6 step 9 (currency-pin enforcement)
 */

import type { Iso4217 } from "@kraahl/contract";

export type { Iso4217 };

export const NATIVE_CURRENCY: Readonly<Record<string, Iso4217>> = {
  // USD
  US: 'USD',
  // GBP
  GB: 'GBP',
  // CAD / AUD / NZD
  CA: 'CAD',
  AU: 'AUD',
  NZ: 'NZD',
  // JPY / TWD
  JP: 'JPY',
  TW: 'TWD',
  // EUR (eurozone members)
  AT: 'EUR',
  BE: 'EUR',
  CY: 'EUR',
  DE: 'EUR',
  EE: 'EUR',
  ES: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  GR: 'EUR',
  HR: 'EUR',
  IE: 'EUR',
  IT: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  LV: 'EUR',
  MT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  SI: 'EUR',
  SK: 'EUR',
  // Other Europe
  CH: 'CHF',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  PL: 'PLN',
  CZ: 'CZK',
  HU: 'HUF',
  // Asia-Pacific
  HK: 'HKD',
  SG: 'SGD',
  KR: 'KRW',
  TH: 'THB',
  MY: 'MYR',
  ID: 'IDR',
  PH: 'PHP',
  VN: 'VND',
  IN: 'INR',
  // MENA / Africa
  AE: 'AED',
  IL: 'ILS',
  ZA: 'ZAR',
  // Latin America
  MX: 'MXN',
  BR: 'BRL',
  AR: 'ARS',
  CL: 'CLP',
  CO: 'COP',
  // Türkiye
  TR: 'TRY',
};

export function nativeCurrency(country: string): Iso4217 | null {
  if (!country) return null;
  return NATIVE_CURRENCY[country.toUpperCase()] ?? null;
}
